import { Box, Button, Card, Chip, Divider, Grid, IconButton, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getBaseURLWithAuth } from "../../common/utils/session";
import { IHeaderGrid } from "../../interfaces/grid/IHeaderGrid";
import GridContent from "../../components/grid";
import GridHeader from "../../components/grid/header";
import { useEffect, useState } from "react";
import useLazyLoading from "../../common/hooks/loader";
import Loader from "../../components/loader";
import { ITransactionDto } from "../../interfaces/transaction/ITransactionDto";
import { defaultControllerPagination, IPagination } from "../../interfaces/pagination/IPagination";
import { tryDeleteTransaction, tryGetTransactionListPaginated } from "../../services/transaction/transaction.service";
import { useNotification } from "../../components/notification/useNotification";
import extractErrorMessage from "../../common/utils/errorMessages";
import { TransactionTypeEnum } from "../../common/enum/transaction-type-enum";
import { formatPriceToDolar } from "../../common/utils/number";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from "date-fns";
import { colorByTransactionType } from "../../common/utils/color";
import Pagination from "../../components/grid/pagination";
import TransactionFilters from "./filters";
import { useGlobalContext } from "../../context/global-context";

const headerColumnList: IHeaderGrid[] = [
    {
        align: 'left',
        name: 'Cripto Moeda',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Data',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Tipo Op',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Carteira',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Qtd Moeda',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Valor Fiat',
        padding: 'normal'
    },

    {
        align: 'left',
        name: 'Preço',
        padding: 'normal'
    },
    {
        align: 'right',
        name: 'Editar/Remover',
        padding: 'normal'
    },
];

export default function Transactions() {
    const navigate = useNavigate();
    const { displayNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const [transactionList, setTransactionList] = useState<ITransactionDto[]>();
    const [rowCount, setRowCount] = useState<number>(0);
    const [pagination, setPagination] = useState<IPagination>(defaultControllerPagination);
    const { state } = useGlobalContext();
    const { transactionFilters } = state;


    useEffect(() => {
        setLoading(true);
        getTransactionListPaginated()
        setLoading(false);
    }, [pagination, transactionFilters])

    async function getTransactionListPaginated() {
        try {
            var response = await tryGetTransactionListPaginated(pagination, transactionFilters);

            if (response?.status === 201) {
                setTransactionList(response.data.results);
                setRowCount(response.data.rowCount);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    const handleRemoveTransaction = async (transactionId: string) => {
        setLoading(true);

        try {
            var response = await tryDeleteTransaction(transactionId);

            if (response?.status === 202) {
                var newTransactionList = transactionList?.filter(x => x.id != transactionId);
                setTransactionList(newTransactionList);

                displayNotification({ type:'success', message: 'Transação eliminada com sucesso' });
            }
        }
        catch (error) {
            displayNotification({ type: 'success', message: extractErrorMessage(error) });
        }
        finally {
            setLoading(false);
        }
    }

    return (
        <>
            {showLoading === true ? <Loader /> : false}
            <Box mt={3} mb={1}>
                <Grid container justifyContent="space-between" alignItems="flex-start">
                    <Grid>
                        <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main', letterSpacing: 1 }}>
                            Gestão de Transações
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            Histórico e registo das tuas transações
                        </Typography>
                    </Grid>
                    <Grid>
                        <Button variant="contained" color="primary" onClick={() => navigate(getBaseURLWithAuth() + 'transaction/create')}>
                            Nova Transação
                        </Button>
                    </Grid>
                </Grid>
                <Divider sx={{ mt: 2, borderColor: 'rgba(255,215,0,0.15)' }} />
            </Box>
            <Box>
                <TransactionFilters />
            </Box>
            <Card sx={{ mt: 2, border: '1px solid rgba(255,215,0,0.15)' }}>
                <Box>
                    <GridContent>
                        <GridHeader headerProps={headerColumnList} />
                        <TableBody>
                            {transactionList && transactionList.map((transaction, index) => (
                                <TableRow
                                    key={transaction.id}
                                    sx={{
                                        backgroundColor: index % 2 !== 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(255,215,0,0.05)' },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <TableCell align="left">
                                        <Grid container alignItems="center" spacing={0.5}>
                                            <Grid>
                                                <img src={transaction.cryptoCurrencyImageUrl} width="18" height="18"/>
                                            </Grid>
                                            <Grid>
                                                <strong>{transaction.cryptoCurrencyName}</strong>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{format(transaction.transactionDate, 'dd/MM/yyyy')}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Chip
                                            label={TransactionTypeEnum[transaction.transactionType]}
                                            size="small"
                                            color={colorByTransactionType(transaction.transactionType) as 'success' | 'error'}
                                            variant="outlined"
                                            sx={{ fontWeight: 600, fontSize: '0.72rem', borderRadius: '6px' }}
                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{transaction.walletName}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{transaction.cryptoCurrencyQuantity}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{formatPriceToDolar(transaction.fiatAmount)}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{formatPriceToDolar(transaction.price)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Grid container justifyContent="flex-end">
                                            <Grid>
                                                <IconButton color="info" onClick={() => navigate(getBaseURLWithAuth() + `transaction/edit/${transaction.id}`)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Grid>
                                            <Grid>
                                                <IconButton color="error" onClick={() => handleRemoveTransaction(transaction.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </GridContent>
                    <Pagination rowCount={rowCount} pagination={pagination} setController={setPagination}/>
                </Box>
            </Card>
        </>
    )
}